import React, { useState } from 'react';

const SoundSafariGame = ({ gameData, onComplete }) => {
    const [shuffledAnimals, setShuffledAnimals] = useState([]);
    const [clickedAnimals, setClickedAnimals] = useState([]);
    const [score, setScore] = useState(0);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showExplanations, setShowExplanations] = useState(false);

    React.useEffect(() => {
        if (gameData?.animals) {
            setShuffledAnimals([...gameData.animals].sort(() => Math.random() - 0.5));
            setClickedAnimals([]);
        }
    }, [gameData]);

    const handleAnimalClick = (animalId) => {
        if (showFeedback) return;
        if (navigator.vibrate) navigator.vibrate(20);
        
        if (clickedAnimals.includes(animalId)) {
            setClickedAnimals(prev => prev.filter(id => id !== animalId));
        } else {
            setClickedAnimals(prev => [...prev, animalId]);
        }
    };

    const checkAnswer = () => {
        if (showFeedback || clickedAnimals.length === 0) return;
        const correctAnimalsIds = gameData.animals
            .filter(animal => animal.has_target_sound)
            .map(animal => animal.id);

        const isCorrect =
            clickedAnimals.length === correctAnimalsIds.length &&
            clickedAnimals.every(id => correctAnimalsIds.includes(id));

        setShowFeedback(true);

        if (isCorrect) {
            const points = (gameData.points_per_animal || 15) * correctAnimalsIds.length;
            const newScore = score + points;
            setScore(newScore);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                onComplete?.(newScore);
            }, 4000);
        } else {
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => {
                setShowFeedback(false);
            }, 3000);
        }
    };

    if (!gameData || shuffledAnimals.length === 0) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-orange-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🦁</div>
                <h3 className="text-base md:text-3xl font-black text-orange-900 tracking-tighter uppercase">Ruošiamas safari...</h3>
            </div>
        );
    }

    const targetSoundsStr = gameData.target_sounds?.join(', ') || '';
    const correctAnimals = gameData.animals.filter(a => a.has_target_sound);
    const progress = (clickedAnimals.length / correctAnimals.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-orange-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            🦁
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                                Garsų Safari
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Surask visus gyvūnus, kurių pavadinimuose girdime nurodytą garsą!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Atradimai</p>
                            <p className="text-2xl font-black text-orange-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Rasta</p>
                            <p className="text-2xl font-black text-slate-800 leading-none">{clickedAnimals.length}/{correctAnimals.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-slate-200/50 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(249,115,22,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Target Sound Card */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-3 md:p-8 text-8xl opacity-[0.03] select-none pointer-events-none">✨</div>
                <div className="text-center space-y-3 md:space-y-8 w-full max-w-3xl">
                    <h4 className="text-xs font-black text-orange-400 uppercase tracking-[0.5em]">Safari Taikinys</h4>
                    <div className="relative group inline-block">
                        <div className="absolute -inset-10 bg-orange-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                        <div className="bg-white px-5 md:px-16 py-3 md:py-8 rounded-[3.5rem] shadow-2xl border-4 border-orange-100 relative z-10 scale-110 group-hover:scale-125 transition-transform duration-500">
                            <span className="text-9xl font-black text-orange-600 leading-none drop-shadow-xl tracking-tighter uppercase">{targetSoundsStr}</span>
                        </div>
                    </div>
                    <div className="flex justify-center gap-4 mt-8">
                        <button
                            onClick={() => { if (navigator.vibrate) navigator.vibrate(20); setShowExplanations(!showExplanations); }}
                            className={`px-8 py-3 rounded-2xl font-black transition-all duration-300 shadow-lg flex items-center gap-3 ${showExplanations ? 'bg-orange-600 text-white' : 'bg-white text-orange-600 border-2 border-orange-100 hover:bg-orange-50'}`}
                        >
                            <span>{showExplanations ? '📖 Slėpti' : '💡 Užuominos'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Animal Selection Stage Area */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-3 md:p-8 text-8xl opacity-[0.05] select-none pointer-events-none text-white">🌿</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:p-6 w-full relative z-10">
                    {shuffledAnimals.map((animal) => {
                        const isClicked = clickedAnimals.includes(animal.id);
                        const isCorrectInFeed = showFeedback && animal.has_target_sound;
                        const isWrongInFeed = showFeedback && isClicked && !animal.has_target_sound;

                        return (
                            <button
                                key={animal.id}
                                onClick={() => handleAnimalClick(animal.id)}
                                disabled={showFeedback}
                                className={`
                                    relative flex flex-col items-center justify-center p-3 md:p-8 rounded-xl md:rounded-[3rem] transition-all duration-500 transform border-b-[12px]
                                    ${isCorrectInFeed
                                        ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-[0_0_50px_rgba(16,185,129,0.5)] z-20'
                                        : isWrongInFeed
                                            ? 'bg-rose-500 text-white border-rose-700 animate-shake scale-95 shadow-[0_0_50px_rgba(244,63,94,0.5)] z-20'
                                            : isClicked
                                                ? 'bg-orange-500 text-white border-orange-700 scale-105 shadow-2xl z-20'
                                                : 'bg-white text-slate-800 border-slate-200 hover:bg-orange-50 active:translate-y-2 active:border-b-0 shadow-lg'}
                                    disabled:cursor-default
                                `}
                            >
                                <div className={`text-8xl mb-4 drop-shadow-2xl ${isClicked ? 'animate-bounce' : ''}`}>
                                    {animal.emoji}
                                </div>
                                <div className={`text-xl font-black uppercase tracking-tighter ${isClicked || (showFeedback && animal.has_target_sound) ? 'text-white' : 'text-slate-800'}`}>
                                    {animal.name}
                                </div>

                                {showExplanations && animal.has_target_sound && !showFeedback && (
                                    <div className="mt-4 w-full bg-slate-100 rounded-2xl p-3 text-[10px] font-black uppercase tracking-widest text-slate-400 border border-slate-200 animate-pop-in">
                                        Garsas: {animal.target_sound} ({animal.sound_position === 'start' ? 'Pradžia' : animal.sound_position === 'middle' ? 'Vidurys' : 'Pabaiga'})
                                    </div>
                                )}

                                {showFeedback && animal.has_target_sound && (
                                    <div className="mt-4 text-xs font-bold leading-tight bg-black/20 p-4 rounded-[1.5rem] text-white/90 italic font-serif">
                                        "{animal.explanation}"
                                    </div>
                                )}

                                {isClicked && !showFeedback && (
                                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-white text-orange-600 rounded-full flex items-center justify-center text-2xl shadow-xl border-4 border-slate-900 animate-pop-in">
                                        🔍
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Submit Controls */}
                <div className="flex gap-2 md:p-6 justify-center mt-12 w-full max-w-2xl relative z-10">
                    <button
                        onClick={() => { if (navigator.vibrate) navigator.vibrate(40); setClickedAnimals([]); }}
                        disabled={clickedAnimals.length === 0 || showFeedback}
                        className="flex-1 py-6 bg-slate-800 text-slate-400 rounded-lg md:rounded-[2.5rem] font-black text-xl border-b-4 border-slate-700 hover:bg-slate-700 active:translate-y-1 active:border-b-0 transition-all disabled:opacity-20"
                    >
                        VALYTI 🗑️
                    </button>
                    <button
                        onClick={checkAnswer}
                        disabled={clickedAnimals.length === 0 || showFeedback}
                        className="flex-[2] py-6 px-12 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg md:rounded-[2.5rem] font-black text-2xl shadow-2xl shadow-orange-500/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-20 uppercase tracking-widest"
                    >
                        BAIGTI SAFARI ✓
                    </button>
                </div>
            </div>

            {/* Global Safari Feedback Overlay */}
            {showFeedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-2xl w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px]
                        ${clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-4 md:p-14 left-1/2 -translate-x-1/2 w-28 h-28 bg-white rounded-full flex items-center justify-center text-2xl md:text-6xl shadow-2xl border-8 border-inherit animate-bounce">
                            {clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? '🦁' : '❓'}
                        </div>
                        
                        <div className="text-center space-y-3 md:space-y-8">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'SAFARI PAVYKO!' : 'TRŪKSTA GYVŪNŲ!'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-2xl font-bold text-slate-700 text-center leading-relaxed">
                                    {clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'Nuostabu! Radai visus safari gyvūnus su ieškomu garsu. Tavo klausa tiesiog nepriekaištinga!' : 'Oi! Panašu, kad radai ne visus gyvūnus arba pasirinkai neteisingus. Peržiūrėk safari gyvūnus dar kartą!'}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">
                                    {clickedAnimals.length === correctAnimals.length && clickedAnimals.every(id => correctAnimals.map(a => a.id).includes(id)) ? 'Keliaujame į kitą safari...' : 'Tęsiame paieškas...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoundSafariGame;
